import React from 'react'
import { createPortal } from 'react-dom'
import styled from '@emotion/styled'
import { identity } from 'lodash'
import { KeyCodes, getScrollParent, getFixedPositionOffsetParent, isChild, isOpenFin } from 'utils'
import { SlidingPanel } from '../panels'
import * as constants from 'view/styles/constants'
import { createMakeTestId } from '../datagrid'

const VERTICAL_MARGIN = 3
const OPENFIN_TITLEBAR_HEIGHT = 40

export const MenuWrapper = styled.div`
  outline: none;
  position: relative;
  display: inline-block;
`

// Use position: fixed to allow the menu to escape from parents with overflow: hidden
const SlidingPanelStyled = styled(SlidingPanel)<{}>`
  position: fixed;
  overflow: auto;
  transition-duration: 100ms;
  border-radius: 3px;
  box-shadow: 0 0 30px 0 rgba(68, 76, 95, 0.1);
  margin: ${VERTICAL_MARGIN}px 0;
  z-index: ${constants.zIndexDropdownMenu};
  & > ul:not(:last-child) {
    border-bottom: 2px solid ${constants.CORE_PRIMARY_2};
  }
`

interface MenuContainerProps {
  primaryComponent: JSX.Element
  menuVisible: boolean
  className?: string
  disabled?: boolean
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>
  onUpdateMenuVisible: (visible: boolean) => void
  dataTestId?: string
  children?: React.ReactNode
  withPortal?: boolean
}

const defaultTestId = 'MenuContainer'

const portalEl = document.createElement('div')
portalEl.setAttribute('data-test-id', 'MenuContainerPortal')
portalEl.setAttribute('id', 'MenuContainerPortal')
document.body.appendChild(portalEl)

export class MenuContainer extends React.Component<MenuContainerProps> {
  private elementRef = React.createRef<HTMLDivElement>()
  private menuRef = React.createRef<HTMLDivElement>()
  private makeTestId: (componentTestId: string) => string

  public constructor(props: MenuContainerProps) {
    super(props)
    this.makeTestId = createMakeTestId(props.dataTestId ?? defaultTestId)
  }

  private adjustMenuPlacement = () => {
    if (!this.menuRef.current || !this.elementRef.current) {
      return
    }

    const element = this.elementRef.current
    const elementRect = element.getBoundingClientRect()
    const elementOffsetParent = element.offsetParent
    const elementScrollParent = getScrollParent(element)
    const elementScrollParentRect = elementScrollParent.getBoundingClientRect()

    const menuOffsetParent = getFixedPositionOffsetParent(this.menuRef.current)
    const menuOffsetParentRect = menuOffsetParent.getBoundingClientRect()

    // If the scroll parent sits in between the offset parent of the element and the offset parent of the menu
    // then we need to take account of the scroll offset when positioning the menu
    const includeScrollInOffset =
      !this.props.withPortal &&
      isChild(menuOffsetParent, elementScrollParent) &&
      elementOffsetParent &&
      !isChild(elementOffsetParent, elementScrollParent)

    const scrollOffsetTop = includeScrollInOffset ? elementScrollParent.scrollTop : 0

    const menuHeight = this.menuRef.current.scrollHeight
    const menuWidth = this.menuRef.current.scrollWidth
    const maybeOpenfinTitlebarHeight = isOpenFin() ? OPENFIN_TITLEBAR_HEIGHT : 0
    const marginAbove = elementRect.top - elementScrollParentRect.top - scrollOffsetTop - VERTICAL_MARGIN * 2 - maybeOpenfinTitlebarHeight
    const marginBelow = elementScrollParentRect.bottom - elementRect.bottom + scrollOffsetTop - VERTICAL_MARGIN * 2

    // any property that might be touched, must be nulled at the begining of this function.
    const menuStyleRef = this.menuRef.current.style
    menuStyleRef.top = ''
    menuStyleRef.bottom = ''
    menuStyleRef.left = ''
    menuStyleRef.right = ''
    menuStyleRef.maxHeight = ''
    menuStyleRef.minWidth = elementRect.width + 'px'

    if (marginBelow >= menuHeight) {
      // There is available margin for the menu to open downward.
      menuStyleRef.top = `${elementRect.height + elementRect.top - menuOffsetParentRect.top - scrollOffsetTop}px`
    } else if (marginAbove >= menuHeight) {
      // There is available margin for the menu to open upward.
      menuStyleRef.bottom = `${elementRect.height - elementRect.bottom + menuOffsetParentRect.bottom + scrollOffsetTop}px`
    } else if (marginAbove > marginBelow) {
      menuStyleRef.bottom = `${elementRect.height - elementRect.bottom + menuOffsetParentRect.bottom + scrollOffsetTop}px`

      menuStyleRef.maxHeight = !this.props.withPortal ? `${marginAbove}px` : '400px'
    } else {
      // The menu is taller than both the margins above and below, however, there is more available margin below.
      menuStyleRef.top = `${elementRect.height + elementRect.top - menuOffsetParentRect.top - scrollOffsetTop}px`

      menuStyleRef.maxHeight = !this.props.withPortal ? `${marginBelow}px` : '400px'
    }

    if (elementRect.left + menuWidth > elementScrollParentRect.right) {
      menuStyleRef.right = '0px'
    } else {
      if (this.props.withPortal) {
        // when renderer via portal 'left' property has to be positioned absolutely
        menuStyleRef.left = `${elementRect.left}px`
      }
    }
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClosingEvent, false)
    document.addEventListener('scroll', this.handleClosingEvent, true)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClosingEvent, false)
    document.removeEventListener('scroll', this.handleClosingEvent, true)
  }

  public componentDidUpdate() {
    this.adjustMenuPlacement()
  }

  private handleClosingEvent = (evt: MouseEvent) => {
    if (this.props.menuVisible) {
      const isTargetWithinMenu = this.menuRef.current && this.menuRef.current.contains(evt.target as Node)
      const isTargetWithinElement = this.elementRef.current && this.elementRef.current.contains(evt.target as Node)

      if (!isTargetWithinMenu && !isTargetWithinElement) {
        this.props.onUpdateMenuVisible(false)
      }
    }
  }

  private handleBlur = (evt: React.FocusEvent<HTMLElement>) => {
    if (
      this.props.menuVisible &&
      this.elementRef.current &&
      evt.relatedTarget &&
      this.menuRef.current !== evt.relatedTarget &&
      !evt.relatedTarget.classList.contains(constants.VIRTUALIZATION) &&
      !isChild(this.elementRef.current, evt.relatedTarget as HTMLElement)
    ) {
      this.props.onUpdateMenuVisible(false)
    }
  }

  private handleKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
    if (!this.props.disabled) {
      if (this.props.menuVisible) {
        if (evt.keyCode === KeyCodes.ESCAPE || evt.keyCode === KeyCodes.ENTER) {
          evt.preventDefault()
          this.props.onUpdateMenuVisible(false)
        }
      } else {
        if (evt.keyCode === KeyCodes.DOWN || evt.keyCode === KeyCodes.UP) {
          evt.preventDefault()
          evt.stopPropagation()
          this.props.onUpdateMenuVisible(true)
        }
      }
    }

    if (this.props.onKeyDown) {
      this.props.onKeyDown(evt)
    }
  }

  private handleKeyPress = () => {
    if (!this.props.disabled) {
      if (!this.props.menuVisible) {
        this.props.onUpdateMenuVisible(true)
      }
    }
  }

  private handleClick = (_evt: React.MouseEvent<HTMLElement>) => {
    if (!this.props.disabled) {
      this.props.onUpdateMenuVisible(!this.props.menuVisible)
    }
  }

  private renderMenuWithPortal = (content: React.ReactNode) => {
    // the portal is rendered conditionally so it is destroyed when closed
    return this.props.menuVisible ? createPortal(content, portalEl) : null
  }

  render() {
    const renderMenu = this.props.withPortal ? this.renderMenuWithPortal : identity

    return (
      <MenuWrapper
        data-test-id={this.props.dataTestId ?? this.makeTestId('MenuWrapper')}
        ref={this.elementRef}
        tabIndex={-1}
        className={this.props.className}
        onClick={this.handleClick}
        onBlur={this.handleBlur}
        onKeyDown={this.handleKeyDown}
        onKeyPress={this.handleKeyPress}>
        {this.props.primaryComponent}
        {renderMenu(
          <SlidingPanelStyled className={this.makeTestId('Menu')} data-test-id={this.makeTestId('Menu')} ref={this.menuRef}>
            {this.props.menuVisible ? this.props.children : null}
          </SlidingPanelStyled>
        )}
      </MenuWrapper>
    )
  }
}
